import React, { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import AppShell from "@/components/AppShell";
import CompaniesPage from "@/pages/CompaniesPage";
import WorkspacePage from "@/pages/WorkspacePage";
import LoginPage from "@/pages/LoginPage";
import { listCompanies } from "@/lib/api";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";

function AppRoutes() {
  const { ready, isAuthenticated } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  const refreshCompanies = useCallback(async () => {
    try {
      const data = await listCompanies();
      setCompanies(data);
    } catch (e) {
      // 401 is handled globally (token clear + login screen). Other errors
      // just leave the list empty — the page itself surfaces error toasts.
      if (e?.response?.status !== 401) {
        console.error("Failed to load companies", e);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) refreshCompanies();
  }, [isAuthenticated, refreshCompanies]);

  // While we're still checking /auth/config show a tiny placeholder rather
  // than briefly flashing the login screen.
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="h-8 w-8 rounded-full border-2 border-neutral-300 border-t-neutral-900 animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route
          element={
            <AppShell
              companies={companies}
              onCompaniesChanged={refreshCompanies}
            />
          }
        >
          <Route
            path="/"
            element={
              <CompaniesPage
                companies={companies}
                loading={loading}
                onChanged={refreshCompanies}
              />
            }
          />
          <Route
            path="/c/:companyId"
            element={<WorkspacePage companies={companies} />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
      <Toaster richColors position="top-right" />
    </div>
  );
}

export default App;
